// src/contexts/AuthContextAWS.js
// AWS Cognito Authentication Context - Simplified

import React, { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import { signIn as cognitoSignIn, signOut as cognitoSignOut, signUp as cognitoSignUp, getCurrentUser, fetchAuthSession, confirmSignIn } from 'aws-amplify/auth';
import awsConfig, { API_BASE_URL } from '../lib/awsConfig';
import { AuthContext } from './SharedAuthContext';
import { logLogin, logLogout, logFailedLogin } from '../services/auditService';

// Configure Amplify
Amplify.configure(awsConfig);

// Re-export useAuth from shared context
export { useAuth } from './SharedAuthContext';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [practice, setPractice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [pendingEmail, setPendingEmail] = useState(null);
  const [managedPractices, setManagedPractices] = useState([]);
  const [selectedPracticeId, setSelectedPracticeId] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    // Restore auth state from localStorage immediately — no spinner on refresh
    const cachedUser = localStorage.getItem('flowrcm_user_cache');
    const cachedPractice = localStorage.getItem('flowrcm_practice_cache');
    if (cachedUser && cachedPractice) {
      try {
        setUser(JSON.parse(cachedUser));
        setPractice(JSON.parse(cachedPractice));
        setLoading(false); // App renders immediately with cached data
      } catch (_) {}
    }

    // Validate session with Cognito in the background
    try {
      const cognitoUser = await getCurrentUser();
      const userObj = {
        id: cognitoUser.userId,
        email: cognitoUser.signInDetails?.loginId || cognitoUser.username,
        username: cognitoUser.username
      };
      setUser(userObj);
      localStorage.setItem('flowrcm_user_cache', JSON.stringify(userObj));

      // Fetch fresh practice data from Lambda in background
      fetchUserData(cognitoUser);
    } catch (error) {
      // Session expired or invalid — clear caches and force login
      localStorage.removeItem('flowrcm_user_cache');
      localStorage.removeItem('flowrcm_practice_cache');
      setUser(null);
      setPractice(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async (cognitoUser) => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.idToken?.toString();

      if (!token) {
        return;
      }

      const controller = new AbortController();
      const fetchTimeout = setTimeout(() => controller.abort(), 5000);
      let response;
      try {
        response = await fetch(`${API_BASE_URL}/user/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          signal: controller.signal,
        });
      } finally {
        clearTimeout(fetchTimeout);
      }

      if (response.ok) {
        const data = await response.json();
        setPractice(data);
        localStorage.setItem('flowrcm_practice_cache', JSON.stringify(data));

        // If org user, fetch managed practices
        const orgRoles = ['billing_admin', 'dso_admin'];
        if (orgRoles.includes(data.role)) {
          const practicesRes = await fetch(`${API_BASE_URL}/org/practices`, {
            headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
          });
          if (practicesRes.ok) {
            const practicesData = await practicesRes.json();
            const practices = practicesData.data || [];
            setManagedPractices(practices);
            // Auto-select first practice if none selected
            if (practices.length > 0 && !selectedPracticeId) {
              setSelectedPracticeId(practices[0].id);
            }
          }
        }
      } else if (response.status === 403) {
        // Explicitly denied — account is pending approval
        setPractice({
          approved: false,
          active: true,
          role: 'user',
          name: 'Pending Setup',
          id: null
        });
      }
      // For 5xx / network errors / cold start timeouts: leave practice as null
      // so the app retries on next render rather than showing "Pending Approval"
    } catch (error) {
      // Network error or Lambda cold start — do not set approved:false
    }
  };

  const signIn = async (email, password) => {
    try {
      let result;
      try {
        result = await cognitoSignIn({ username: email, password });
      } catch (firstError) {
        // If a stale session wasn't fully cleared, clean it up and retry once
        if (firstError.message?.includes('already a signed in user')) {
          await cognitoSignOut();
          result = await cognitoSignIn({ username: email, password });
        } else {
          throw firstError;
        }
      }

      if (result.isSignedIn) {
        // Set user immediately from form data — skip extra getCurrentUser() round trip
        const immediateUser = { id: null, email: email, username: email };
        setUser(immediateUser);
        // Set practice immediately so ProtectedRoute never retries/spins
        const cached = localStorage.getItem('flowrcm_practice_cache');
        let initialPractice = { approved: true, active: true, role: 'user', name: '' };
        if (cached) {
          try { initialPractice = JSON.parse(cached); } catch (_) {}
        }
        setPractice(initialPractice);
        // Background: refresh full user details + practice from Lambda
        getCurrentUser().then(cognitoUser => {
          const userObj = { id: cognitoUser.userId, email: email, username: cognitoUser.username };
          setUser(userObj);
          localStorage.setItem('flowrcm_user_cache', JSON.stringify(userObj));
          fetchUserData(cognitoUser);
        }).catch(() => {});
        logLogin({ email });
        return { user: { email } };
      } else if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true);
        setPendingEmail(email);
        return { needsNewPassword: true };
      } else if (result.nextStep) {
        throw new Error(`Additional step required: ${result.nextStep.signInStep}`);
      }
    } catch (error) {
      throw error;
    }
  };

  const completeNewPassword = async (newPassword) => {
    try {
      const result = await confirmSignIn({ challengeResponse: newPassword });

      if (result.isSignedIn) {
        const cognitoUser = await getCurrentUser();
        setUser({
          id: cognitoUser.userId,
          email: pendingEmail,
          username: cognitoUser.username
        });
        setNeedsNewPassword(false);
        setPendingEmail(null);
        await fetchUserData(cognitoUser);
        logLogin({ email: pendingEmail, newPasswordSet: true });
        return { user: cognitoUser };
      }
    } catch (error) {
      throw error;
    }
  };

  const signUp = async (email, password, practiceData) => {
    try {
      const result = await cognitoSignUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email
          }
        }
      });
      return result;
    } catch (error) {
      throw error;
    }
  };

  const switchPractice = (practiceId) => {
    setSelectedPracticeId(practiceId);
    const found = managedPractices.find(p => p.id === practiceId);
    if (found) setPractice(prev => ({ ...prev, practice_id: practiceId, name: found.name }));
  };

  const signOut = async () => {
    try {
      logLogout();
      await cognitoSignOut();
    } catch (error) {
      // continue with cleanup even if signOut fails
    } finally {
      setUser(null);
      setPractice(null);
      setManagedPractices([]);
      setSelectedPracticeId(null);
      sessionStorage.clear();
      localStorage.removeItem('flowrcm_user_cache');
      // Keep practice cache — used to show dashboard instantly on next login
    }
  };

  const refreshPractice = async () => {
    if (user) {
      try {
        const cognitoUser = await getCurrentUser();
        await fetchUserData(cognitoUser);
      } catch (e) {
        // Refresh failed
      }
    }
  };

  const value = {
    user,
    practice,
    loading,
    authError,
    signIn,
    signUp,
    signOut,
    refreshPractice,
    needsNewPassword,
    completeNewPassword,
    managedPractices,
    selectedPracticeId,
    switchPractice,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
